// client/src/ChatBox.js
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthProvider';
import { API_BASE } from './api';
import { toolbarButton, toolbarButtonActive } from './toolbarButton';
import {
  currentPushEndpoint,
  followEvent,
  isFollowing,
  notificationPermission,
  pushSupport,
  unfollowEvent,
} from './pushNotifications';
import { formatDateTime } from './formatDate';
import { assignCommentColours } from './commentColours';

export default function ChatBox({ eventId, name: nameProp, isPublic = false, commentsLocked = false }) {
  const auth = useAuth();
  const token = auth?.token;
  // Only signed-in accounts can comment, on any event (the server checks too).
  const mustSignIn = !token;
  // Closed events (e.g. the example) show their discussion but take no new
  // comments or replies (the server refuses them too).
  const canComment = !commentsLocked && !mustSignIn;
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');
  const [guestName, setGuestName] = useState(() => localStorage.getItem('chatName') || '');
  const [loading, setLoading] = useState(false);

  // Comment notifications for this event on this device.
  const support = useMemo(() => pushSupport(), []);
  const [following, setFollowing] = useState(() => isFollowing(eventId));
  const [followBusy, setFollowBusy] = useState(false);
  const [followMessage, setFollowMessage] = useState('');
  const [showFollowPrompt, setShowFollowPrompt] = useState(false);

  useEffect(() => {
    setFollowing(isFollowing(eventId));
    setFollowMessage('');
    setShowFollowPrompt(false);
  }, [eventId]);

  const toggleFollow = async () => {
    setFollowMessage('');
    setShowFollowPrompt(false);

    if (support === 'ios-needs-install') {
      setFollowMessage('On iPhone, add Frolik to your Home Screen (Share → Add to Home Screen), open it from there, then tap Notify me.');
      return;
    }

    setFollowBusy(true);
    try {
      if (following) {
        await unfollowEvent(eventId);
        setFollowing(false);
        setFollowMessage('Notifications are off for this event.');
      } else {
        await followEvent(eventId, token);
        setFollowing(true);
        setFollowMessage('You’ll be notified when someone comments here.');
      }
    } catch (err) {
      setFollowMessage(err.message || 'Something went wrong.');
    } finally {
      setFollowBusy(false);
    }
  };

  // After someone comments, offer to notify them about replies.
  const offerFollow = () => {
    if (!following && support !== 'unsupported' && notificationPermission() !== 'denied') {
      setShowFollowPrompt(true);
    }
  };

  // Each person's colour in this discussion.
  const colourFor = useMemo(() => assignCommentColours(comments), [comments]);

  const currentName = useMemo(() => {
    return (nameProp || guestName || '').trim();
  }, [nameProp, guestName]);

  const loadComments = async () => {
    if (!eventId) return;

    try {
      const res = await fetch(`${API_BASE}/api/events/${encodeURIComponent(eventId)}/comments`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = await res.json();

      if (Array.isArray(data.comments)) {
        setComments(data.comments);
      } else {
        setComments([]);
      }
    } catch (err) {
      console.error('Failed to load comments:', err);
    }
  };

  useEffect(() => {
    loadComments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, token]);

  const saveGuestName = (value) => {
    const clean = value.trim().slice(0, 24);
    setGuestName(clean);
    localStorage.setItem('chatName', clean);
  };

  const postComment = async (e) => {
    e.preventDefault();

    const message = text.trim();
    const author = currentName || 'Guest';

    if (!message) return;

    setLoading(true);

    try {
      const notifyEndpoint = await currentPushEndpoint();
      const res = await fetch(`${API_BASE}/api/events/${encodeURIComponent(eventId)}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ author, message, notifyEndpoint }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to post comment');
      }

      setComments(Array.isArray(data.comments) ? data.comments : []);
      setText('');
      offerFollow();
    } catch (err) {
      console.error('Failed to post comment:', err);
      alert('Failed to post comment');
    } finally {
      setLoading(false);
    }
  };

  const postReply = async (parentCommentId, message) => {
    const notifyEndpoint = await currentPushEndpoint();
    const res = await fetch(`${API_BASE}/api/events/${encodeURIComponent(eventId)}/comments`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        author: currentName || 'Guest',
        message,
        parentCommentId,
        notifyEndpoint,
      }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || 'Failed to post reply');
    }

    setComments(Array.isArray(data.comments) ? data.comments : []);
    offerFollow();
  };

  return (
    <section className="event-chat" style={styles.wrap}>
      <div style={styles.header}>
        <div>
          <div className="chat-kicker" style={styles.kicker}>Comments</div>
          <h3 className="chat-title" style={styles.title}>Event discussion</h3>
        </div>

        {support !== 'unsupported' && !commentsLocked && (
          <button
            type="button"
            onClick={toggleFollow}
            disabled={followBusy}
            aria-pressed={following}
            title={following ? 'Tap to stop notifications for this event' : 'Get a notification when someone comments'}
            style={{
              ...toolbarButton,
              ...(following ? toolbarButtonActive : {}),
              opacity: followBusy ? 0.6 : 1,
            }}
          >
            {following ? 'Notifications on' : 'Notify me'}
          </button>
        )}
      </div>

      {followMessage && (
        <div role="status" className="chat-note" style={styles.followMessage}>{followMessage}</div>
      )}

      {!nameProp && canComment && (
        <div style={styles.nameBox}>
          <label className="chat-label" style={styles.nameLabel}>Your name</label>
          <input
            value={guestName}
            onChange={(e) => saveGuestName(e.target.value)}
            placeholder="Name for comments"
            maxLength={24}
            style={styles.nameInput}
          />
        </div>
      )}

      {commentsLocked ? (
        <div className="chat-note" role="status" style={styles.signInPrompt}>
          <span>Comments are closed on this event.</span>
        </div>
      ) : mustSignIn ? (
        <div className="chat-note" style={styles.signInPrompt}>
          <span>Sign in to join the discussion.</span>
          {auth?.signIn && (
            <button type="button" onClick={() => auth.signIn()} style={toolbarButton}>
              Sign in
            </button>
          )}
        </div>
      ) : (
      <form onSubmit={postComment} style={styles.form}>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={currentName ? 'Leave a comment…' : 'Enter a name first…'}
          disabled={!currentName || loading}
          style={styles.textarea}
        />

        <button
          type="submit"
          disabled={!text.trim() || !currentName || loading}
          style={{
            ...styles.postButton,
            opacity: !text.trim() || !currentName || loading ? 0.5 : 1,
          }}
        >
          {loading ? 'Posting…' : 'Post comment'}
        </button>
      </form>
      )}

      {showFollowPrompt && (
        <div className="chat-note" style={styles.followPrompt}>
          <span>Get notified when someone replies?</span>
          <button type="button" onClick={toggleFollow} disabled={followBusy} style={toolbarButton}>
            Notify me
          </button>
          <button
            type="button"
            onClick={() => setShowFollowPrompt(false)}
            style={{ ...toolbarButton, background: 'transparent', boxShadow: 'none', border: '1px solid transparent' }}
          >
            No thanks
          </button>
        </div>
      )}

      <div style={styles.list}>
        {comments.length ? (
          comments.map((comment) => (
            <CommentThread
              key={comment.id || comment._id}
              comment={comment}
              depth={0}
              currentName={currentName}
              onReply={canComment ? postReply : null}
              colourFor={colourFor}
            />
          ))
        ) : (
          <div className="chat-empty" style={styles.empty}>No comments yet. Be the first.</div>
        )}
      </div>
    </section>
  );
}

function CommentThread({ comment, depth, currentName, onReply, colourFor }) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');

  const submitReply = async (event) => {
    event.preventDefault();
    const message = replyText.trim();
    if (!message || posting) return;

    setPosting(true);
    setError('');

    try {
      await onReply(comment.id || comment._id, message);
      setReplyText('');
      setReplyOpen(false);
    } catch (replyError) {
      setError(replyError.message || 'Failed to post reply');
    } finally {
      setPosting(false);
    }
  };

  const replies = Array.isArray(comment.replies) ? comment.replies : [];
  const colour = colourFor(comment.author);

  return (
    <div style={depth ? styles.replyThread : undefined}>
      <article
        style={{
          ...(depth ? styles.replyCard : styles.commentCard),
          // This person's colour: a bar down the left of each of their comments.
          borderLeft: `4px solid ${colour.accent}`,
        }}
      >
        <div style={styles.commentTopRow}>
          <div style={styles.commentTop}>
            <strong className="chat-author" style={{ ...styles.author, color: colour.text }}>{comment.author || 'Guest'}</strong>
            <span className="chat-time" style={styles.time}>
              {comment.createdAt
                ? formatDateTime(comment.createdAt)
                : ''}
            </span>
          </div>
          {onReply && (
          <button
            type="button"
            aria-label={`Reply to ${comment.author || 'Guest'}'s comment`}
            aria-expanded={replyOpen}
            title="Reply to this comment"
            onClick={() => {
              setReplyOpen((open) => !open);
              setError('');
            }}
            style={styles.replyButton}
          >
            +
          </button>
          )}
        </div>

        <p className="chat-message" style={styles.message}>{comment.message}</p>

        {replyOpen && onReply && (
          <form onSubmit={submitReply} style={styles.replyForm}>
            <textarea
              value={replyText}
              onChange={(event) => setReplyText(event.target.value)}
              placeholder={currentName ? `Reply to ${comment.author || 'Guest'}…` : 'Enter a name first…'}
              disabled={!currentName || posting}
              rows={3}
              autoFocus
              style={styles.replyTextarea}
            />
            {error && <div role="alert" style={styles.replyError}>{error}</div>}
            <button
              type="submit"
              disabled={!replyText.trim() || !currentName || posting}
              style={{ ...styles.replySubmit, opacity: !replyText.trim() || !currentName || posting ? 0.5 : 1 }}
            >
              {posting ? 'Replying…' : 'Post reply'}
            </button>
          </form>
        )}
      </article>

      {replies.length > 0 && (
        <div style={styles.replies}>
          {replies.map((reply) => (
            <CommentThread
              key={reply.id || reply._id}
              comment={reply}
              depth={depth + 1}
              currentName={currentName}
              onReply={onReply}
              colourFor={colourFor}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  wrap: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(255,255,255,0.72)',
    borderRadius: 14,
    border: '1px solid rgba(255,255,255,0.5)',
    padding: 18,
    boxSizing: 'border-box',
    fontFamily: "'Reddit Mono', monospace",
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
    marginBottom: 14,
  },

  followMessage: {
    margin: '-6px 0 12px',
    color: '#98551f',
    fontSize: 12,
  },

  signInPrompt: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 16,
    color: '#98551f',
    fontSize: 12,
  },

  followPrompt: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 10,
    color: '#98551f',
    fontSize: 12,
  },

  kicker: {
    fontSize: 13,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#999',
    marginBottom: 4,
  },

  title: {
    margin: 0,
    fontSize: 22,
    color: '#000',
  },

  nameBox: {
    marginBottom: 12,
  },

  nameLabel: {
    display: 'block',
    fontSize: 12,
    color: '#777',
    marginBottom: 6,
  },

  nameInput: {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #ddd',
    borderRadius: 7,
    padding: '12px 14px',
    fontFamily: "'Reddit Mono', monospace",
    fontSize: 14,
    outline: 'none',
  },

  form: {
    marginBottom: 16,
  },

  textarea: {
    width: '100%',
    minHeight: 90,
    boxSizing: 'border-box',
    border: '1px solid #ddd',
    borderRadius: 9,
    padding: 14,
    fontFamily: "'Reddit Mono', monospace",
    fontSize: 14,
    resize: 'vertical',
    outline: 'none',
    background: '#fff',
    color: '#000',
  },

  // Same look as the footer's Admin / Sign out buttons.
  postButton: {
    ...toolbarButton,
    display: 'block',
    marginLeft: 'auto',
    marginTop: 10,
  },

  // Grows as comments are added; the page scrolls, not the box.
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    paddingRight: 4,
  },

  commentCard: {
    background: '#fff',
    border: '1px solid #e5e5e5',
    borderRadius: 14,
    padding: '18px 20px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
  },

  replyThread: {
    marginLeft: 18,
    paddingLeft: 14,
    borderLeft: '2px solid rgba(255,122,0,0.3)',
  },

  replyCard: {
    background: '#fffaf4',
    border: '1px solid #f0e4d6',
    borderRadius: 9,
    padding: '14px 16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.035)',
  },

  replies: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    marginTop: 10,
  },

  commentTopRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 10,
  },

  replyButton: {
    flex: '0 0 30px',
    width: 30,
    height: 30,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: 0,
    padding: 0,
    border: '1px solid rgba(17,24,39,0.15)',
    borderRadius: '50%',
    background: 'linear-gradient(180deg, #fff 0%, #f1f1f1 100%)',
    color: '#111',
    fontSize: 20,
    lineHeight: 1,
    cursor: 'pointer',
    boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
  },

  replyForm: {
    marginTop: 12,
    paddingTop: 12,
    borderTop: '1px solid #eee5da',
  },

  replyTextarea: {
    width: '100%',
    minHeight: 64,
    boxSizing: 'border-box',
    border: '1px solid #ddd',
    borderRadius: 5,
    padding: 10,
    fontFamily: 'Arial, Helvetica, sans-serif',
    fontSize: 14,
    lineHeight: 1.4,
    resize: 'vertical',
    outline: 'none',
    background: '#fff',
    color: '#000',
  },

  replySubmit: {
    ...toolbarButton,
    display: 'block',
    margin: '8px 0 0 auto',
  },

  replyError: {
    marginTop: 6,
    color: '#b42318',
    fontSize: 12,
  },

  commentTop: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    marginBottom: 14,
  },

  author: {
    fontSize: 20,
    color: '#000',
  },

  time: {
    fontSize: 14,
    color: '#999',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
  },

  message: {
    margin: 0,
    fontFamily: 'Helvetica, Arial, sans-serif',
    fontSize: 18,
    lineHeight: 1.45,
    color: '#000',
    whiteSpace: 'pre-wrap',
  },

  empty: {
    padding: 20,
    textAlign: 'center',
    color: '#777',
    fontSize: 14,
  },
};